import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { 
  StarIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  ChartBarIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import api from '../services/api';

const AdminReviews = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [filterContractor, setFilterContractor] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('');
  const [projectPage, setProjectPage] = useState(1);
  const [projectFilter, setProjectFilter] = useState('all'); // all, reviewed, unreviewed
  const queryClient = useQueryClient();

  // 프로젝트 검색 디바운스
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProjectSearch(projectSearch);
      setProjectPage(1); // 검색 시 첫 페이지로 리셋
    }, 500);

    return () => clearTimeout(timer);
  }, [projectSearch]);

  // 리뷰 목록 조회
  const { data: reviewsData, isLoading } = useQuery(
    ['adminReviews', search, filterContractor, sortBy, sortOrder, page, pageSize],
    () => api.get('/reviews', {
      params: {
        search,
        contractor_id: filterContractor,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit: pageSize
      }
    }).then(res => res.data),
    { keepPreviousData: true }
  );

  const reviews = reviewsData?.reviews || [];

  // 소장 목록 조회 (필터용)
  const { data: contractorsData } = useQuery(
    'contractorsForFilter',
    () => api.get('/admin/contractors').then(res => res.data)
  );
  
  const contractors = contractorsData?.contractors || [];

  // 완료된 프로젝트 목록 조회
  const { data: completedProjectsData, isLoading: isLoadingProjects } = useQuery(
    ['completedProjects', debouncedProjectSearch, projectPage],
    () => api.get('/admin/projects', {
      params: {
        status: 'completed',
        search: debouncedProjectSearch,
        page: projectPage,
        limit: 10
      }
    }).then(res => res.data),
    {
      enabled: showAddModal
    }
  );

  const completedProjects = completedProjectsData?.projects || [];
  
  // 필터링된 프로젝트 목록
  const filteredProjects = completedProjects.filter(project => {
    const hasReview = reviews.some(r => r.project_id === project.id);
    if (projectFilter === 'reviewed') return hasReview;
    if (projectFilter === 'unreviewed') return !hasReview;
    return true; // all
  });

  // 리뷰 삭제 mutation
  const deleteMutation = useMutation(
    (reviewId) => api.delete(`/reviews/${reviewId}`),
    {
      onSuccess: () => {
        toast.success('평가가 삭제되었습니다.');
        queryClient.invalidateQueries('adminReviews');
        setShowDeleteModal(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '삭제에 실패했습니다.');
      }
    }
  );

  // 리뷰 수정 mutation
  const updateMutation = useMutation(
    ({ reviewId, rating, comment }) => api.put(`/reviews/${reviewId}`, { rating, comment }),
    {
      onSuccess: () => {
        toast.success('평가가 수정되었습니다.');
        queryClient.invalidateQueries('adminReviews');
        setShowEditModal(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '수정에 실패했습니다.');
      }
    }
  );

  // 리뷰 생성 mutation
  const createMutation = useMutation(
    ({ projectId, rating, comment }) => api.post(`/reviews/projects/${projectId}`, { rating, comment }),
    {
      onSuccess: () => {
        toast.success('평가가 추가되었습니다.');
        queryClient.invalidateQueries('adminReviews');
        setShowAddModal(false);
        setSelectedProject(null);
        setNewRating(0);
        setNewComment('');
        setProjectSearch('');
        setProjectPage(1);
        setProjectFilter('all');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '평가 추가에 실패했습니다.');
      }
    }
  );

  // 통계 데이터 계산
  const stats = {
    totalReviews: reviews.length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : '0.0',
    ratingDistribution: [1, 2, 3, 4, 5].map(rating => ({
      rating,
      count: reviews.filter(r => r.rating === rating).length
    }))
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < rating) {
        return <StarSolid key={i} className="h-4 w-4 text-yellow-400" />;
      }
      return <StarIcon key={i} className="h-4 w-4 text-gray-300" />;
    });
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">평가 관리</h1>
          <p className="text-gray-600 mt-1">고객이 작성한 소장 평가를 관리합니다.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">표시 개수:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={25}>25개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
            </select>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <StarSolid className="h-5 w-5" />
            평가 추가
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 평가</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalReviews}개</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">평균 평점</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-2xl font-bold text-gray-900">{stats.avgRating}</p>
                <div className="flex">{renderStars(Math.round(stats.avgRating))}</div>
              </div>
            </div>
            <StarSolid className="h-8 w-8 text-yellow-400" />
          </div>
        </div>

        {/* 평점 분포 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 md:col-span-2">
          <p className="text-sm text-gray-600 mb-3">평점 분포</p>
          <div className="space-y-2">
            {stats.ratingDistribution.reverse().map(({ rating, count }) => (
              <div key={rating} className="flex items-center gap-2">
                <div className="flex items-center gap-1 w-16">
                  <span className="text-sm text-gray-600">{rating}</span>
                  <StarSolid className="h-4 w-4 text-yellow-400" />
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-yellow-400 rounded-full"
                    style={{ width: `${stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {count}개
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* 검색 */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="프로젝트명, 고객명, 평가 내용 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* 소장 필터 */}
          <select
            value={filterContractor}
            onChange={(e) => setFilterContractor(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">전체 소장</option>
            {contractors?.map((contractor) => (
              <option key={contractor.id} value={contractor.id}>
                {contractor.name} ({contractor.company_name})
              </option>
            ))}
          </select>

          {/* 정렬 */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [newSortBy, newSortOrder] = e.target.value.split('-');
              setSortBy(newSortBy);
              setSortOrder(newSortOrder);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="created_at-desc">최신순</option>
            <option value="created_at-asc">오래된순</option>
            <option value="rating-desc">평점 높은순</option>
            <option value="rating-asc">평점 낮은순</option>
          </select>
        </div>
      </div>

      {/* 리뷰 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">평가 목록을 불러오는 중...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-8 text-center">
            <StarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">평가가 없습니다.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {reviews.map((review) => (
              <div key={review.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* 프로젝트 정보 */}
                    <div className="mb-3">
                      <h3 className="font-medium text-gray-900">{review.project_title}</h3>
                      <p className="text-sm text-gray-600">{review.project_address}</p>
                    </div>

                    {/* 평가 정보 */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{review.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BuildingStorefrontIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {review.contractor_name} ({review.contractor_company})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {format(new Date(review.created_at), 'yyyy년 MM월 dd일', { locale: ko })}
                        </span>
                      </div>
                    </div>

                    {/* 평점 및 코멘트 */}
                    <div className="flex items-start gap-3">
                      <div className="flex">{renderStars(review.rating)}</div>
                      {review.comment && (
                        <p className="text-sm text-gray-700 flex-1">{review.comment}</p>
                      )}
                    </div>
                  </div>

                  {/* 액션 버튼들 */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => {
                        setShowEditModal(review);
                        setEditRating(review.rating);
                        setEditComment(review.comment || '');
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="평가 수정"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(review)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="평가 삭제"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {reviewsData && reviewsData.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <button
              onClick={() => setPage(Math.min(reviewsData.pages, page + 1))}
              disabled={page === reviewsData.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                전체 <span className="font-medium">{reviewsData.total}</span>개 중{' '}
                <span className="font-medium">{((page - 1) * pageSize) + 1}</span> -{' '}
                <span className="font-medium">{Math.min(page * pageSize, reviewsData.total)}</span> 표시
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">이전</span>
                  ◀
                </button>
                
                {(() => {
                  const totalPages = reviewsData.pages;
                  const maxButtons = 7;
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageButtons = [];
                  
                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === page
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageButtons;
                })()}
                
                <button
                  onClick={() => setPage(Math.min(reviewsData.pages, page + 1))}
                  disabled={page === reviewsData.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">다음</span>
                  ▶
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">평가 삭제 확인</h3>
            <p className="text-gray-600 mb-4">
              이 평가를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-gray-900">{showDeleteModal.project_title}</p>
              <p className="text-sm text-gray-600">
                {showDeleteModal.customer_name} → {showDeleteModal.contractor_name}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex">{renderStars(showDeleteModal.rating)}</div>
                {showDeleteModal.comment && (
                  <p className="text-sm text-gray-600">"{showDeleteModal.comment}"</p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => deleteMutation.mutate(showDeleteModal.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">평가 수정</h3>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-gray-900">{showEditModal.project_title}</p>
              <p className="text-sm text-gray-600">
                {showEditModal.customer_name} → {showEditModal.contractor_name}
              </p>
            </div>

            {/* 평점 선택 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">평점</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setEditRating(star)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    {star <= editRating ? (
                      <StarSolid className="h-8 w-8 text-yellow-400" />
                    ) : (
                      <StarIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">{editRating}점</span>
              </div>
            </div>

            {/* 평가 내용 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">평가 내용</label>
              <textarea
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="평가 내용을 입력하세요..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(null);
                  setEditRating(0);
                  setEditComment('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => updateMutation.mutate({
                  reviewId: showEditModal.id,
                  rating: editRating,
                  comment: editComment
                })}
                disabled={editRating === 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                수정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 평가 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">평가 추가</h3>
            
            {!selectedProject ? (
              <>
                {/* 필터 버튼 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setProjectFilter('all');
                        setProjectPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        projectFilter === 'all' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      전체
                    </button>
                    <button
                      onClick={() => {
                        setProjectFilter('unreviewed');
                        setProjectPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        projectFilter === 'unreviewed' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      평가 안됨
                    </button>
                    <button
                      onClick={() => {
                        setProjectFilter('reviewed');
                        setProjectPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        projectFilter === 'reviewed' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      평가 완료
                    </button>
                  </div>
                </div>

                {/* 프로젝트 검색 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">프로젝트 검색</label>
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="프로젝트명, 주소, 고객명으로 검색..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* 프로젝트 목록 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">완료된 프로젝트 선택</label>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    {isLoadingProjects ? (
                      <div className="p-4 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-gray-500 text-sm">프로젝트 목록을 불러오는 중...</p>
                      </div>
                    ) : filteredProjects.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {projectFilter === 'unreviewed' 
                          ? '평가되지 않은 프로젝트가 없습니다.' 
                          : projectFilter === 'reviewed' 
                            ? '평가된 프로젝트가 없습니다.'
                            : '완료된 프로젝트가 없습니다.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredProjects.map((project) => {
                          // 이미 평가가 있는지 확인
                          const hasReview = reviews.some(r => r.project_id === project.id);
                          return (
                            <button
                              key={project.id}
                              onClick={() => setSelectedProject(project)}
                              className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                            >
                              <div className="font-medium text-gray-900">{project.title}</div>
                              <div className="text-sm text-gray-600">
                                {project.address} | {project.customer_name} → {project.contractor_name}
                              </div>
                              {hasReview && (
                                <div className="text-xs text-orange-600 mt-1">이미 평가가 존재합니다</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* 페이징 */}
                  {completedProjectsData && completedProjectsData.pages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        전체 {completedProjectsData.total}개 중 {((projectPage - 1) * 10) + 1}-{Math.min(projectPage * 10, completedProjectsData.total)}개
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setProjectPage(p => Math.max(1, p - 1))}
                          disabled={projectPage === 1}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          이전
                        </button>
                        <span className="text-sm text-gray-600">
                          {projectPage} / {completedProjectsData.pages}
                        </span>
                        <button
                          onClick={() => setProjectPage(p => Math.min(completedProjectsData.pages, p + 1))}
                          disabled={projectPage === completedProjectsData.pages}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          다음
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* 선택된 프로젝트 정보 */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedProject.title}</p>
                      <p className="text-sm text-gray-600">
                        {selectedProject.customer_name} → {selectedProject.contractor_name}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedProject(null);
                        setNewRating(0);
                        setNewComment('');
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      변경
                    </button>
                  </div>
                </div>

                {/* 평점 선택 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">평점</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setNewRating(star)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        {star <= newRating ? (
                          <StarSolid className="h-8 w-8 text-yellow-400" />
                        ) : (
                          <StarIcon className="h-8 w-8 text-gray-300" />
                        )}
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{newRating}점</span>
                  </div>
                </div>

                {/* 평가 내용 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">평가 내용</label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="평가 내용을 입력하세요..."
                  />
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedProject(null);
                  setNewRating(0);
                  setNewComment('');
                  setProjectSearch('');
                  setProjectPage(1);
                  setProjectFilter('all');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              {selectedProject && (
                <button
                  onClick={() => {
                    const hasReview = reviews.some(r => r.project_id === selectedProject.id);
                    if (hasReview) {
                      if (window.confirm('이미 평가가 존재합니다. 계속하시겠습니까?')) {
                        createMutation.mutate({
                          projectId: selectedProject.id,
                          rating: newRating,
                          comment: newComment
                        });
                      }
                    } else {
                      createMutation.mutate({
                        projectId: selectedProject.id,
                        rating: newRating,
                        comment: newComment
                      });
                    }
                  }}
                  disabled={newRating === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  평가 추가
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;